import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useMatch } from 'react-router-dom';
import ProjectsHeader from '../components/ProjectsHeader';
import NavigationMenu from '../components/NavigationMenu';
import MainContentArea from '../components/MainContentArea';
import MainPage from '../pages/MainPage';
import DataEmbeddingsPage from '../pages/DataEmbeddingsPage';
import DataReviewPage from '../pages/DataReviewPage';
import DataManagementPage from '../pages/DataManagementPage';
import '../styles/MainPage.css';
import { buildMainNavLinks } from '../utils/mainNavLinks';
import './HorizonLayout.css';
import { API_BASE_URL, getAuthHeaders, getUserGoogleAuthStatus, driveAccessFailureHint } from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { startActivityAwarePolling } from '../utils/activityAwarePolling';

function HorizonLayout() {
  const { appendGroupToUrl } = useGroup();
  const { user } = useAuth();
  const horizonNotifications = useNotifications();
  const [activeTab, setActiveTab] = useState('main');
  const navigate = useNavigate();
  
  // Initialize menu state from cookie
  const getInitialOpen = () => {
    try {
      const match = document.cookie.match(/(?:^|; )nav_open=([^;]*)/);
      if (match) {
        const v = decodeURIComponent(match[1]);
        return v === '1' || v === 'true';
      }
    } catch (_) {}
    return false;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(getInitialOpen);
  const [disableAnim, setDisableAnim] = useState(true);

  useEffect(() => {
    // Enable animations after first paint
    const id = requestAnimationFrame(() => setDisableAnim(false));
    return () => cancelAnimationFrame(id);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      const next = !prev;
      try {
        document.cookie = `nav_open=${next ? '1' : '0'}; path=/; max-age=15552000`;
      } catch (_) {}
      return next;
    });
  };

  // Use useMatch to cleanly extract the tab parameter from the URL
  const matchWithWildcard = useMatch('/horizon/:tab/*');
  const matchWithoutWildcard = useMatch('/horizon/:tab');
  const matchRoot = useMatch('/horizon');
  const match = matchWithWildcard || matchWithoutWildcard || matchRoot;
  
  const [googleAuthStatus, setGoogleAuthStatus] = useState({
    isConfigured: false,
    isAuthenticated: false,
    canAccessDrive: false,
    loading: true,
    connectedUser: null,
    driveAccessFailureReason: null
  });
  const [systemStatus, setSystemStatus] = useState('not_connected');
  const statusLoadingRef = useRef(false);

  useEffect(() => {
    if (!googleAuthStatus.loading) {
      window.dispatchEvent(new CustomEvent('googleDriveStatusUpdate', {
        detail: {
          isAuthenticated: googleAuthStatus.isAuthenticated,
          canAccessDrive: googleAuthStatus.canAccessDrive
        }
      }));
    }
  }, [googleAuthStatus.isAuthenticated, googleAuthStatus.canAccessDrive, googleAuthStatus.loading]);

  // Sync active tab with URL sub-route under /horizon/* and support deep links + back/forward
  useEffect(() => {
    const tab = match?.params?.tab || '';

    // If no sub-route, normalize URL to /horizon/main
    if (!tab) {
      navigate('/horizon/main', { replace: true });
      setActiveTab('main');
      return;
    }

    const allowedTabs = ['main', 'data-embeddings', 'data-review', 'data-management'];

    if (!allowedTabs.includes(tab)) {
      // Invalid tab; redirect to main
      navigate('/horizon/main', { replace: true });
      setActiveTab('main');
      return;
    }

    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [match, navigate, activeTab]);

  useEffect(() => {
    const checkStatus = async () => {
      if (statusLoadingRef.current) {
        return;
      }

      statusLoadingRef.current = true;

      try {
        let userAuthData = null;
        try {
          userAuthData = await getUserGoogleAuthStatus();
        } catch (e) {
          console.warn('[HorizonLayout] User Google auth status:', e.message);
        }

        if (userAuthData?.success) {
          setGoogleAuthStatus(prev => {
            const newStatus = {
              isConfigured: userAuthData.isConfigured || false,
              isAuthenticated: userAuthData.isAuthenticated || false,
              canAccessDrive: userAuthData.canAccessDrive || false,
              loading: false,
              connectedUser: userAuthData.connectedUser || null,
              driveAccessFailureReason: userAuthData.driveAccessFailureReason || null
            };

            if (prev.isConfigured === newStatus.isConfigured &&
                prev.isAuthenticated === newStatus.isAuthenticated &&
                prev.canAccessDrive === newStatus.canAccessDrive &&
                prev.loading === newStatus.loading &&
                prev.connectedUser?.email === newStatus.connectedUser?.email &&
                prev.driveAccessFailureReason === newStatus.driveAccessFailureReason) {
              return prev;
            }

            return newStatus;
          });
        } else {
          setGoogleAuthStatus({
            isConfigured: false,
            isAuthenticated: false,
            canAccessDrive: false,
            loading: false,
            connectedUser: null,
            driveAccessFailureReason: null
          });
        }

        const statsHeaders = await getAuthHeaders();
        const statsResponse = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/stats`), {
          method: 'POST',
          headers: statsHeaders,
          body: JSON.stringify({})
        });

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();

          if (statsData.success && statsData.stats) {
            const nextStatus = statsData.stats.systemStatus || 'not_connected';
            setSystemStatus((prev) => (prev === nextStatus ? prev : nextStatus));
          } else {
            setSystemStatus('not_connected');
          }
        } else {
          setSystemStatus('not_connected');
        }

      } catch (error) {
        if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
          setGoogleAuthStatus({
            isConfigured: false,
            isAuthenticated: false,
            canAccessDrive: false,
            loading: false,
            connectedUser: null,
            driveAccessFailureReason: null
          });
          setSystemStatus('not_connected');
        }
      } finally {
        statusLoadingRef.current = false;
      }
    };

    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get('oauth_success');
    const oauthError = urlParams.get('oauth_error');

    if (oauthSuccess === 'true' || oauthSuccess === 'personal') {
      window.history.replaceState({}, document.title, window.location.pathname);
      checkStatus().catch(err => {
        console.error('[HorizonLayout] Error in initial checkStatus:', err);
      });
    } else if (oauthError) {
      console.error('[HorizonLayout] OAuth error:', decodeURIComponent(oauthError));
      window.history.replaceState({}, document.title, window.location.pathname);
      checkStatus().catch(err => {
        console.error('[HorizonLayout] Error in initial checkStatus:', err);
      });
    } else {
      checkStatus().catch(err => {
        console.error('[HorizonLayout] Error in initial checkStatus:', err);
      });
    }

    const stopPolling = startActivityAwarePolling(
      () => {
        if (statusLoadingRef.current) return;
        return checkStatus().catch((err) => {
          console.error('[HorizonLayout] Error in polling check:', err);
        });
      },
      60_000,
      // Initial checkStatus already ran above; only schedule subsequent polls.
      { immediate: false }
    );

    const handleRefreshStats = () => {
      if (!statusLoadingRef.current) {
        checkStatus();
      }
    };
    window.addEventListener('refreshStats', handleRefreshStats);

    const handleDataReviewUpdate = () => {
      if (!statusLoadingRef.current) {
        checkStatus();
      }
    };
    window.addEventListener('dataReviewUpdate', handleDataReviewUpdate);

    // Listen for horizon changes and refresh stats
    const handleHorizonChanged = () => {
      console.log('[HorizonLayout] Horizon changed, refreshing stats...');
      if (!statusLoadingRef.current) {
        checkStatus();
      }
    };
    window.addEventListener('horizonChanged', handleHorizonChanged);

    return () => {
      stopPolling();
      window.removeEventListener('refreshStats', handleRefreshStats);
      window.removeEventListener('dataReviewUpdate', handleDataReviewUpdate);
      window.removeEventListener('horizonChanged', handleHorizonChanged);
    };
  }, [appendGroupToUrl]);

  const getSystemStatusDisplay = () => {
    const isConnected = googleAuthStatus.isAuthenticated && googleAuthStatus.canAccessDrive;
    const isProcessing = systemStatus === 'processing';

    if (isConnected && !isProcessing) {
      return { text: 'Ready', color: '#10b981' };
    }
    if (isProcessing) {
      return { text: 'Processing', color: '#f59e0b' };
    }
    return { text: 'Not Connected', color: '#ef4444' };
  };

  const systemStatusDisplay = getSystemStatusDisplay();

  const renderGoogleStatusLine = () => {
    if (googleAuthStatus.loading) {
      return <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Checking Google Drive…</>;
    }
    if (googleAuthStatus.isAuthenticated && googleAuthStatus.canAccessDrive) {
      const email = googleAuthStatus.connectedUser?.email;
      return (
        <>
          <i className="fas fa-check-circle" aria-hidden="true" /> Google connected
          {email ? <> — <span className="horizon-status-email">{email}</span></> : null}
        </>
      );
    }
    if (googleAuthStatus.isAuthenticated) {
      const hint = driveAccessFailureHint(googleAuthStatus.driveAccessFailureReason);
      const email = googleAuthStatus.connectedUser?.email;
      return (
        <>
          <i className="fas fa-exclamation-triangle" aria-hidden="true" /> Google signed in — Drive not reachable
          {email ? <> ({email})</> : null}
          {hint ? <span className="horizon-status-hint" title={hint}> — {hint}</span> : null}
        </>
      );
    }
    return (
      <Link to="/settings/user" className="horizon-status-link">
        <i className="fas fa-times-circle" aria-hidden="true" /> Google not connected
      </Link>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'main':
        return <MainPage />;
      case 'data-embeddings':
        return <DataEmbeddingsPage />;
      case 'data-review':
        return <DataReviewPage />;
      case 'data-management':
        return <DataManagementPage />;
      default:
        return <MainPage />;
    }
  };

  return (
    <div className="horizon-layout">
      <ProjectsHeader
        isMenuOpen={isMenuOpen}
        onToggle={toggleMenu}
        title="Horizon"
      />
      <div className="horizon-container">
        <NavigationMenu
          isOpen={isMenuOpen}
          onToggle={toggleMenu}
          disableTransition={disableAnim}
          linkItems={buildMainNavLinks({
            user,
            horizonBadge: horizonNotifications.totalCount,
            embeddingCount: horizonNotifications.embeddingCount,
            reviewCount: horizonNotifications.reviewCount,
            schedulerErrorCount: horizonNotifications.schedulerErrorCount,
          })}
          menuItems={[]}
          activeItem={activeTab}
          onItemClick={(id) => {
            setActiveTab(id);
            navigate(`/horizon/${id}`);
          }}
          showUserMenu={true}
        />
        <MainContentArea isMenuOpen={isMenuOpen} disableTransition={disableAnim}>
          <div className="horizon-main-content">
            <div
              className={`horizon-status-bar ${
                googleAuthStatus.isAuthenticated && googleAuthStatus.canAccessDrive
                  ? 'horizon-status-bar--connected'
                  : googleAuthStatus.isAuthenticated
                    ? 'horizon-status-bar--warning'
                    : 'horizon-status-bar--disconnected'
              }`}
            >
              <span className="horizon-status-google">
                {renderGoogleStatusLine()}
              </span>
              <span className="horizon-status-sep" aria-hidden="true">·</span>
              <span className="horizon-status-system" style={{ color: systemStatusDisplay.color }}>
                {systemStatusDisplay.text === 'Processing' && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
                {systemStatusDisplay.text === 'Ready' && <i className="fas fa-check-circle" aria-hidden="true" />}
                {systemStatusDisplay.text === 'Not Connected' && <i className="fas fa-times-circle" aria-hidden="true" />}
                {' '}System {systemStatusDisplay.text}
              </span>
            </div>

            {/* Main content */}
            {renderContent()}
          </div>
        </MainContentArea>
      </div>
    </div>
  );
}

export default HorizonLayout;
